import { PermissionAction, Permissions } from "@/constants/access";
import { useSessionStore } from "@/stores/session";
import { UserPermissions } from "@/types/partner";

export const arePermissionObjectsEqual = (
    obj1: Record<string, string[] | undefined>,
    obj2: Record<string, string[] | undefined>,
): boolean => {
    /**
     * Compares two permission objects for deep equality
     * Each object is expected to have string keys (e.g. permission names)
     * and arrays of strings as values (e.g. granular permissions like "view", "edit").
     * The comparison ensures:
     * - Both objects have the same keys.
     * - Each corresponding array of permissions contains the same values, regardless of order.
     * @param {Record<string, string[] | undefined>} obj1
     * @param {Record<string, string[] | undefined>} obj2
     * @returns {boolean}
     */
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;
    if (!keys1.every((key) => keys2.includes(key))) return false;

    for (const key of keys1) {
        const arr1 = (obj1[key] ?? []).slice().sort();
        const arr2 = (obj2[key] ?? []).slice().sort();

        if (arr1.length !== arr2.length) return false;
        if (!arr1.every((val, idx) => val === arr2[idx])) return false;
    }

    return true;
};

export const extractCorePermissions = (
    storedPermissions: UserPermissions | null,
): string[] => {
    /**
     * Extracts and returns the top-level core permission keys from the given stored permissions
     * object. Returns a list of permission keys (e.g., "base", "payroll", "supplier") as strings.
     * Returns an empty array if input is null.
     * @param {UserPermissions | null} storedPermissions
     * @returns {string[]}
     */
    return storedPermissions === null ? [] : Object.keys(storedPermissions);
};

export function hasRequiredPermissions(
    requiredPermissions: Permissions[],
): boolean {
    /**
     * Checks whether all required permissions exist as keys in the given permissions object.
     * @param {Permissions[]} requiredPermissions
     * @returns {boolean}
     */
    const sessionStore = useSessionStore();
    const permissionsObject = sessionStore.permissions ?? {};
    return requiredPermissions.every((perm) => perm in permissionsObject);
}

export const permissionExists = (
    key: string,
    permissionsObject: Record<string, string[] | undefined>,
): boolean => {
    /**
     * Checks if a given string exists as a key in the provided permissions object.
     * @param {string} key
     * @param { Record<string, string[] | undefined>} permissionsObject
     * @returns {boolean}
     */
    return Object.prototype.hasOwnProperty.call(permissionsObject, key);
};

export const isActionAllowed = (
    permissionKey: string,
    action: PermissionAction,
): boolean => {
    /**
     * Checks if a specific action is allowed for a given permission key.
     * @param {string} permissionKey - the key representing a specific permission group.
     * @param {PermissionAction} action - the action to check (e.g., VIEW, EDIT).
     * @returns {boolean}
     */
    const sessionStore = useSessionStore();
    const permissions = sessionStore.permissions ?? {};
    const allowedActions = permissions[permissionKey];
    return Array.isArray(allowedActions) && allowedActions.includes(action);
};

export const isEditAllowed = (permissionKey: string): boolean => {
    /**
     * Determines if the EDIT action is allowed for a specific permission key.
     * @param {string} permissionKey - the key representing a specific permission group.
     * @returns {boolean}
     */
    const sessionStore = useSessionStore();
    const permissions = sessionStore.permissions ?? {};
    const allowedActions = permissions[permissionKey];
    return (
        Array.isArray(allowedActions) &&
        allowedActions.includes(PermissionAction.EDIT)
    );
};
